/**
 * Loading / empty / error presentation for data-backed sections.
 *
 * Skeletons rather than spinners: the layout stays the same size while data
 * arrives, so the page does not jump once it lands.
 */

export function SkeletonCard({ className = "" }) {
  return (
    <div
      className={`animate-pulse border border-charcoal/60 rounded overflow-hidden ${className}`}
      aria-hidden="true"
    >
      <div className="aspect-[16/10] bg-charcoal/40" />
      <div className="p-5 space-y-3">
        <div className="h-3 w-20 bg-charcoal/50 rounded" />
        <div className="h-5 w-3/4 bg-charcoal/50 rounded" />
        <div className="h-3 w-full bg-charcoal/40 rounded" />
        <div className="h-3 w-5/6 bg-charcoal/40 rounded" />
      </div>
    </div>
  );
}

export function SkeletonRows({ count = 3 }) {
  return (
    <div className="space-y-3" aria-hidden="true">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="animate-pulse border border-charcoal/60 rounded p-5">
          <div className="h-4 w-2/3 bg-charcoal/50 rounded" />
          <div className="mt-3 h-3 w-full bg-charcoal/40 rounded" />
        </div>
      ))}
    </div>
  );
}

/**
 * Renders the right state for a remote collection.
 *
 * `error` is intentionally quiet: the service layer falls back to bundled
 * content, so by the time this component sees an error the page is usually
 * still showing something useful. A red banner would be alarming and unhelpful.
 */
export default function AsyncSection({
  loading,
  isEmpty,
  skeleton,
  emptyTitle = "Nothing here yet",
  emptyMessage,
  children,
}) {
  if (loading) {
    return skeleton ?? <SkeletonRows />;
  }

  if (isEmpty) {
    return (
      <div className="border border-charcoal/60 rounded py-16 px-6 text-center">
        <p className="font-display font-bold text-white">{emptyTitle}</p>
        {emptyMessage && <p className="mt-2 text-sm text-mutedtext">{emptyMessage}</p>}
      </div>
    );
  }

  return children;
}
